import React from 'react';
import {
  DocPage, PageHeader, Section, Prerequisites,
  Para, Callout, NextStep, Tooltip,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './Product.css';

const TOC = [
  { id: 'overview',         label: 'Overview' },
  { id: 'contents',         label: 'What makes a product billable' },
  { id: 'tag-dims',         label: 'Pricing dimensions' },
  { id: 'usage-based',      label: 'Usage-based billing' },
  { id: 'multi',            label: 'Multiple products' },
];

// ── Product list ──────────────────────────────────────────────────────────────

function ProductList({ companyName, products, primaryProduct }) {
  return (
    <div className="prod-list">
      <div className="prod-list-header">
        <span className="prod-list-company">{companyName}</span>
      </div>
      <div className="prod-list-items">
        {products.map((p) => (
          <div key={p} className={`prod-list-item${p === primaryProduct ? ' prod-list-item--primary' : ''}`}>
            <span className="prod-list-dot" />
            <span className="prod-list-name">{p}</span>
            {p === primaryProduct && <span className="prod-list-badge">used in examples below</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Single-tag example table ──────────────────────────────────────────────────

function SingleTagTable({ tagDim, tagValues, feeComponents }) {
  return (
    <div className="prod-table-wrap">
      <table className="prod-table">
        <thead>
          <tr>
            <th className="prod-th">Fee</th>
            {tagValues.map((v) => (
              <th key={v} className="prod-th prod-th--right">{tagDim} = {v}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {feeComponents.map((fc) => (
            <tr key={fc.name} className="prod-tr">
              <td className="prod-td prod-td--name">
                <span className="prod-fc-name">{fc.name}</span>
                <span className="prod-fc-unit">per {fc.unit}</span>
              </td>
              {fc.rates.map((r, i) => (
                <td key={i} className="prod-td prod-td--rate">{r}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Multi-tag example ─────────────────────────────────────────────────────────

function MultiTagGrid({ tags }) {
  const [t1, t2] = tags;
  const combos = t1.values.flatMap((v1) => t2.values.map((v2) => [v1, v2]));
  return (
    <div className="prod-table-wrap">
      <table className="prod-table">
        <thead>
          <tr>
            <th className="prod-th">{t1.label}</th>
            <th className="prod-th">{t2.label}</th>
          </tr>
        </thead>
        <tbody>
          {combos.map(([v1, v2], i) => (
            <tr key={i} className="prod-tr">
              <td className="prod-td">{v1}</td>
              <td className="prod-td">{v2}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Multi-product tree ────────────────────────────────────────────────────────

function ProductTree({ companyName, tree }) {
  return (
    <div className="prod-tree">
      <div className="prod-tree-company">{companyName}</div>
      {tree.map((product, pi) => (
        <div key={pi} className="prod-tree-product">
          <div className="prod-tree-product-line">
            <span className="prod-tree-branch">{pi < tree.length - 1 ? '├──' : '└──'}</span>
            <span className="prod-tree-product-name">{product.name}</span>
          </div>
          <div className="prod-tree-children">
            <div className="prod-tree-child">
              <span className="prod-tree-child-label">Tags:</span>
              <span className="prod-tree-child-value">{product.tags.join(', ')}</span>
            </div>
            <div className="prod-tree-child prod-tree-child--last">
              <span className="prod-tree-child-label">Fees:</span>
              <span className="prod-tree-child-value">{product.components.join(', ')}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Usage event card ──────────────────────────────────────────────────────────

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Product({ onNavigate }) {
  const { industry } = useIndustry();
  const { product: p } = industry;
  const description =`Each service you sell needs its own pricing rules, usage metrics, and invoice line items. A Product is the scope that groups all of that together — one product per thing you sell.`;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Products"
        title="Product"
        description={description}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'concepts', label: 'Core Concepts' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          A business rarely sells just one thing. {p.companyName}, for example, offers{' '}
          {p.products.slice(0, -1).join(', ')}, and {p.products[p.products.length - 1]} — each
          with its own fee structure,{' '}
          <Tooltip tip="Attributes defined on a product that cause a fee's price to vary across different values. Each unique combination gets its own rate card slot.">
            pricing dimensions
          </Tooltip>
          , and customer base. Each of these is modeled as a separate product.
        </Para>
        <ProductList
          companyName={p.companyName}
          products={p.products}
          primaryProduct={p.primaryProduct}
        />
        <Para>
          Fee components, pricing dimensions, and meters are all scoped to a product —
          they gain meaning from the product they're associated with and cannot be shared across products.
        </Para>
      </Section>

      <Section id="contents" title="What makes a product billable">
        <Para>
          A product on its own cannot generate revenue. Three things must be associated with it:
        </Para>
        <Para>
          <strong>Fees</strong> — describe how the product is billed. Each fee
          represents one billable service and appears as a line item on the customer's invoice.
          The {p.primaryProduct} product is billed through{' '}
          {p.singleTagExample.feeComponents.map((fc) => fc.name).join(', ')}.
        </Para>
        <Para>
          <strong>Pricing Dimensions</strong> — define the pricing axes along which charges can vary.
          If {p.primaryProduct} prices differently by{' '}
          {p.singleTagExample.tagDim.toLowerCase()}, that pricing dimension is defined at the product level
          and each fee opts in to the attributes that affect its price.
        </Para>
        <Para>
          <strong>Meters</strong> (when usage-based) — describe how the product is
          measured. Without at least one meter, the billing engine has no quantity to
          bill against.
        </Para>
      </Section>

      <Section id="tag-dims" title="Pricing dimensions">
        <Para>
          Pricing dimensions are defined on the product — not on individual fees. This is
          the shared pool of pricing axes. Each fee opts in to the subset that affects
          its price.
        </Para>
        <Para>
          For example, {p.primaryProduct} defines a{' '}
          <strong>{p.singleTagExample.tagDim}</strong> pricing dimension with values{' '}
          {p.singleTagExample.tagValues.join(', ')}. All fees that use this attribute
          get a separate rate per value:
        </Para>
        <SingleTagTable
          tagDim={p.singleTagExample.tagDim}
          tagValues={p.singleTagExample.tagValues}
          feeComponents={p.singleTagExample.feeComponents}
        />
        <Callout type="tip">
          A fee does not have to use every pricing dimension on the product. If Tracking
          Charges only varies by {p.singleTagExample.tagDim} and not by other attributes, it
          simply declares only {p.singleTagExample.tagDim}.
        </Callout>
        <Para>
          When pricing varies across more than one axis, define multiple pricing dimensions. Adding{' '}
          {p.multiTagExample.tags.map((t) => t.label).join(' and ')} to {p.primaryProduct}{' '}
          produces {p.multiTagExample.tags[0].values.length * p.multiTagExample.tags[1].values.length}{' '}
          combinations ({p.multiTagExample.tags.map((t) => t.values.length).join(' × ')}), each
          priceable independently:
        </Para>
        <MultiTagGrid tags={p.multiTagExample.tags} />
        <Para>
          See <strong>Pricing Dimensions</strong> for the full reference on how attributes and
          combinations work.
        </Para>
      </Section>

      <Section id="usage-based" title="Usage-based billing">
        <Para>
          A product can be configured as <strong>usage-based</strong>. When this setting is on,
          the product's billing amount changes with consumption — and you are required to define
          at least one <strong>Meter</strong>.
        </Para>
        <Para>
          A Meter defines the dimension along which consumption is measured. For example,
          a {p.primaryProduct} product might measure consumption as{' '}
          <em>{p.singleTagExample.feeComponents[0]?.unit ?? 'deliveries'}</em>. The billing system
          uses these measurements to determine the quantity billed for each fee.
        </Para>
        <Callout type="info" title="Meters describe how the product is measured">
          Meters are defined at the product level — not on individual fees.
          A meter without a product has no context; a usage-based product without usage
          attributes has no quantity to bill against. Once defined, a meter can drive one
          or more fees associated with the same product.
        </Callout>
        <Para>
          If usage-based billing is turned off, the product uses fixed pricing only — no usage
          data is expected and no meters are needed.
        </Para>
      </Section>

      <Section id="multi" title="Multiple products">
        <Para>
          Each product has its own independent fees and pricing dimensions. A customer can
          be billed under multiple products in the same billing cycle.
        </Para>
        <ProductTree companyName={p.companyName} tree={p.tree} />
        <Callout type="info">
          A customer consuming services across multiple products in the same cycle will see
          separate line items — or separate invoices, depending on configuration — for each product.
        </Callout>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'pricing-dimensions',  title: 'Pricing Dimensions',    desc: 'Pricing Dimensions are the pricing axes — Zone, Tier, Brand — that let a single fee carry different prices.' },
          { id: 'meter', title: 'Meters',  desc: 'For usage-based products, define the measurement dimensions that drive consumption billing.' },
          { id: 'fee',   title: 'Fees',    desc: 'Each component is one billable service within this product. Configure it before adding a rate card.' },
        ]}
      />
    </DocPage>
  );
}
